import "react-app-polyfill/stable";

import { ThemeProvider } from "@emotion/react";
import type { SolanaTransactionError, UseSailArgs } from "@saberhq/sail";
import { SailProvider } from "@saberhq/sail";
import * as Sentry from "@sentry/react";
import type { PublicKey } from "@solana/web3.js";
import { SUNNY_ADDRESSES, SUNNY_IDLS } from "@sunnyaggregator/sunny-sdk";
import { mapValues } from "lodash";
import React from "react";
import invariant from "tiny-invariant";

import { App } from "./App";
import { ConfigProvider } from "./contexts/config";
import { PricesProvider } from "./contexts/prices";
import { RouterProvider } from "./contexts/router";
import type { ProgramKey } from "./contexts/sdk";
import { SDKProvider } from "./contexts/sdk";
import { SettingsProvider } from "./contexts/settings";
import { SunnyPoolsProvider } from "./contexts/sunnyPools";
import { WalletConnectorProvider } from "./contexts/wallet";
import { theme } from "./theme";
import { parseIdlErrors, ProgramError } from "./utils/anchorError";
import { notify } from "./utils/notifications";
import { EnvironmentProvider } from "./utils/useEnvironment";

const programErrors = mapValues(SUNNY_IDLS, (prog) => parseIdlErrors(prog));

const programIDs = Object.entries(SUNNY_ADDRESSES).reduce(
  (acc, [name, prog]: [name: string, prog: PublicKey]) => ({
    ...acc,
    [prog.toString()]: name,
  }),
  {}
) as Record<string, ProgramKey>;

const onTxSend: UseSailArgs["onTxSend"] = ({ network, pending, message }) => {
  notify({
    message,
    txids: pending.map((p) => p.signature),
    env: network,
  });
};

const onTxError = (error: SolanaTransactionError) => {
  // Log the program error
  const err = error.originalError;
  if (err.toString().includes(": custom program error:")) {
    const txs = error.txs;
    const firstTX = txs[0];
    invariant(firstTX, "first tx");

    // todo: figure out the duplicates
    txs.forEach((tx, i) => {
      const inspectLink = tx.generateInspectLink(
        error.network !== "localnet" ? error.network : "mainnet-beta"
      );
      console.error(`Inspect TX #${i + 1}: ${inspectLink}`);
    });

    console.error("Transactions", txs);
    const progError = ProgramError.parse(
      err,
      firstTX,
      programIDs,
      programErrors
    );
    if (progError) {
      const message = err.message.split(":")[1] ?? "Transaction failed";
      notify({
        message,
        description: `${progError.message}`,
        env: error.network,
        type: "error",
      });
      const sentryArgs = {
        tags: {
          program: progError.program ?? "AnchorInternal",
          "program.error.code": progError.code,
          "program.error.name": progError.errorName,
        },
        extra: {
          progError,
          message,
          originalError: err,
        },
      } as const;
      console.error(progError, sentryArgs);
      Sentry.captureException(progError, sentryArgs);
      return;
    }
  }

  if (/(.+)?: custom program error: 0x1$/.exec(err.message.toString())) {
    notify({
      message: `Insufficient SOL (need more SOL)`,
      description: error.message,
      env: error.network,
      type: "error",
    });
  } else if (err.message.includes("Signature request denied")) {
    notify({
      message: `Transaction cancelled`,
      description: error.message,
      env: error.network,
      type: "info",
    });
    return;
  } else {
    notify({
      message: `Transaction failed (try again later)`,
      description: error.message,
      env: error.network,
      type: "error",
    });
    error.txs.forEach((tx, i) => {
      const inspectLink = tx.generateInspectLink(
        error.network !== "localnet" ? error.network : "mainnet-beta"
      );
      console.error(`Inspect TX #${i + 1}: ${inspectLink}`);
    });
  }
  const sentryArgs = {
    tags: {
      "tx.error": error.tag,
    },
    fingerprint: error.fingerprint,
  } as const;
  console.error(error, sentryArgs);
  Sentry.captureException(error, sentryArgs);
};

export const AppWithProviders: React.FC = () => {
  return (
    <React.StrictMode>
      <ConfigProvider>
        <ThemeProvider theme={theme}>
          <WalletConnectorProvider>
            <EnvironmentProvider>
              <SailProvider
                initialState={{
                  onTxSend,
                  onTxError,
                  onGetMultipleAccountsError: (err) =>
                    Sentry.captureException(err),
                }}
              >
                <SDKProvider>
                  <PricesProvider>
                    <SettingsProvider>
                      <RouterProvider>
                        <SunnyPoolsProvider>
                          <App />
                        </SunnyPoolsProvider>
                      </RouterProvider>
                    </SettingsProvider>
                  </PricesProvider>
                </SDKProvider>
              </SailProvider>
            </EnvironmentProvider>
          </WalletConnectorProvider>
        </ThemeProvider>
      </ConfigProvider>
    </React.StrictMode>
  );
};
